import { useEffect } from 'react';

import { isModalOpen } from 'src/utils/is-modal-open';
import { isUserTyping } from 'src/utils/is-user-typing';
import analyticsClient, { AnalyticsEvent } from 'src/services/analytics';
import type { IConversation } from 'src/api/landlord-messages-api';

type Props = {
    visibleConversations: IConversation[];
    focusedConversation?: IConversation;
    setFocusedConversation: React.Dispatch<React.SetStateAction<IConversation | undefined>>;
};

/**
 * Navigate up and down the list of conversations with the arrow keys.
 */
export const useNavigateConversationsWithArrowKeys = ({
    visibleConversations,
    focusedConversation,
    setFocusedConversation
}: Props) => {

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !isModalOpen() && !isUserTyping()) {
                analyticsClient.track(AnalyticsEvent.SHORTCUT_KEY_USED, {
                    page: 'Conversations',
                    key: event.key,
                });
                // event.preventDefault();
                const currentIndex = visibleConversations.findIndex(conversation => {
                    return conversation.lead_uuid === focusedConversation?.lead_uuid || '';
                });

                if (currentIndex === -1) {
                    // If focused conversation not in current view, start from first
                    if (visibleConversations.length > 0) {
                        setFocusedConversation(visibleConversations[0]);
                    }
                }
                else{
                    switch (event.key) {
                        case 'ArrowDown': {
                            const nextConversation = visibleConversations[currentIndex + 1];
                            if (nextConversation) {
                                setFocusedConversation(nextConversation);
                            }
                            break;
                        }
                        case 'ArrowUp': {
                            const prevConversation = visibleConversations[currentIndex - 1];
                            if (prevConversation) {
                                setFocusedConversation(prevConversation);
                            }
                            break;
                        }
                    }
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [visibleConversations, focusedConversation, setFocusedConversation]);
};
